import { Component, Input, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { IDataSourceDefinitionProperty } from '../../../../../../shared/interfaces/data-source/data-source-definitions/data-source-property-definition.interface';
import { ITableSortDirection } from '../../../../../../shared/interfaces/table/table-sort.interface';
import { NewDataSourceDefinitionsService } from '../../../../data-source-definitions/services/new-data-source-definitions.service';
import { IUIDataSourceItem } from '../../../../data-source-items/interfaces/ui-data-source-item';
import { DataSourceItemChipsService } from '../../../../data-source-items/services/data-source-item-chips.service';
import { NewDataSourceItemsService } from '../../../../data-source-items/services/new-data-source-items.service';

@Component({
  standalone: false,
  selector: 'suvo-bi-property',
  templateUrl: './property.component.html',
  styleUrls: ['./property.component.scss'],
})
export class PropertyComponent {
  @Input() property: IDataSourceDefinitionProperty;
  @Input() item: IUIDataSourceItem;
  @Input() control: FormControl;
  @Input() editItemMode: boolean;
  @Input() config: any = {};

  value: any;
  touched = false;

  referenceOptions: IUIDataSourceItem[];
  referencesLoading = false;

  isChippable = false;
  isReference = false;
  referenceDisplayProperty: string = 'name';
  sanitizedHtmlValue = '';

  constructor(
    private dataSourceItemChipsService: DataSourceItemChipsService,
    private dataSourceDefinitionsService: NewDataSourceDefinitionsService,
    private dataSourceItemService: NewDataSourceItemsService,
  ) {}

  ngOnChanges(simpleChanges: SimpleChanges) {
    this.setValue();

    if (simpleChanges.control) {
      this.loadReferenceData();
    }
  }

  setValue() {
    this.isChippable = !!this.property.displayOptions?.isChippable;

    if (this.property.referenceOptions?.formDefinitionId) {
      this.referenceDisplayProperty = this.property.referenceOptions.displayProperty;
      this.isReference = true;
    }

    const key = this.property.name;

    this.value = this.control?.value ?? this.item?.data?.[key];
    this.sanitizedHtmlValue = this.getSanitizedHtml(this.value);

    this.touched = this.control && this.control.value !== this.item?.data?.[key];
  }

  isHtmlType(): boolean {
    return this.property?.type === 'HTML';
  }

  private getSanitizedHtml(value: unknown): string {
    if (!this.isHtmlType() || typeof value !== 'string' || !value.trim()) {
      return '';
    }

    if (typeof DOMParser === 'undefined') {
      return this.escapeHtml(value);
    }

    const allowedTags = new Set([
      'p',
      'br',
      'strong',
      'em',
      'b',
      'i',
      'u',
      'ul',
      'ol',
      'li',
      'a',
      'h1',
      'h2',
      'h3',
      'h4',
      'blockquote',
      'code',
      'pre',
    ]);
    const blockedTags = new Set(['script', 'style', 'iframe', 'object', 'embed', 'form']);
    const parser = new DOMParser();
    const documentRef = parser.parseFromString(value, 'text/html');

    const sanitizeElement = (element: Element): void => {
      const tagName = element.tagName.toLowerCase();

      if (blockedTags.has(tagName)) {
        element.remove();
        return;
      }

      if (!allowedTags.has(tagName)) {
        const parent = element.parentNode;

        if (!parent) {
          element.remove();
          return;
        }

        while (element.firstChild) {
          parent.insertBefore(element.firstChild, element);
        }

        element.remove();
        return;
      }

      for (const attribute of [...element.attributes]) {
        const attributeName = attribute.name.toLowerCase();

        if (attributeName.startsWith('on') || attributeName === 'style') {
          element.removeAttribute(attribute.name);
          continue;
        }

        if (tagName === 'a') {
          if (!['href', 'title'].includes(attributeName)) {
            element.removeAttribute(attribute.name);
          }
          continue;
        }

        element.removeAttribute(attribute.name);
      }

      if (tagName === 'a') {
        const href = (element.getAttribute('href') || '').trim();
        const isAllowedHref = /^(https?:|mailto:|tel:)/i.test(href);

        if (!isAllowedHref) {
          element.removeAttribute('href');
        }

        element.setAttribute('target', '_blank');
        element.setAttribute('rel', 'noopener noreferrer');
      }
    };

    const walk = (node: Node): void => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        sanitizeElement(node as Element);
      }

      if (!node.parentNode) {
        return;
      }

      for (const child of [...node.childNodes]) {
        walk(child);
      }
    };

    for (const child of [...documentRef.body.childNodes]) {
      walk(child);
    }

    return documentRef.body.innerHTML;
  }

  private escapeHtml(value: string): string {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  hasValue(value: unknown): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    if (this.property.displayOptions?.zeroIsNull && value === 0) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return true;
  }

  isHttpLink(value: unknown): value is string {
    if (typeof value !== 'string') {
      return false;
    }

    return value.startsWith('http://') || value.startsWith('https://');
  }

  trackObject(index: number, object: IUIDataSourceItem) {
    return object._id.toString();
  }

  getChipStyles(value: any, property: IDataSourceDefinitionProperty) {
    return this.dataSourceItemChipsService.getChipStylesFromValue(value, property);
  }

  async loadReferenceData() {
    if (!this.control || this.referencesLoading) {
      return;
    }

    this.referencesLoading = true;

    if (!this.property.referenceOptions?.formDefinitionId) {
      return;
    }

    const referenceOptions = this.property.referenceOptions;
    const value = this.value;

    const definition = await this.dataSourceDefinitionsService.getOne(
      null,
      referenceOptions.formDefinitionId,
    );
    const referenceDataSource = await this.dataSourceItemService.getPaginated(definition.alias, {
      sort: {
        active: `data.${this.referenceDisplayProperty}`,
        direction: ITableSortDirection.Ascending,
      },
    });

    this.referenceOptions = referenceDataSource.data;
    if (this.hasValue(value)) {
      this.control.patchValue(this.referenceOptions.find((d) => value._id === d._id));
    }
  }
}
